import * as Sentry from '@sentry/browser'

import { IDeliveryTicketResponse, IQuery } from '~/client/graph'
import { GetDeliveryTicketDocument } from '~/client/graph/operations/generated/DeliveryTickets.generated'
import config from '~/client/src/shared/Config'
import IDeliveryTicketParams from '~/client/src/shared/models/IDeliveryTicketParams'
import InitialState from '~/client/src/shared/stores/InitialState'
import { captureReportedError } from '~/client/src/shared/utils/Sentry'
import {
  generateTraceId,
  getCurrentTraceId,
} from '~/client/src/shared/utils/traceId'

import RootStore from '../Root.store'
import { EffectMap, EventHandler } from './BaseEvents.store'
import {
  COMPLETE_REQUEST,
  GET_DELIVERY_TICKET,
  GET_DELIVERY_TICKET_RESULT,
  REPORT_ERROR,
} from './eventConstants'
import EventTypes from './eventTypes'

export const handleCompleteRequest = (state, eventType) => {
  state.loading.set(eventType, false)
}

const IOS_CONNECTION_ERROR = 'Error: The network connection was lost.'

export const handleReportError = (
  state: InitialState,
  eventType: EventTypes,
  error: unknown,
): void => {
  // Get existing trace ID or generate new one for non-GraphQL crashes
  let traceId = getCurrentTraceId()
  if (!traceId) {
    traceId = generateTraceId()
  }

  // Store for UI display on error pages
  state.setLastTraceId(traceId)

  if (config.ENVIRONMENT === 'development') {
    console.error(`[TraceId: ${traceId}]`, error, eventType)
    return
  }

  Sentry.withScope(scope => {
    scope.setTag('event', eventType)
    scope.setTag('struxhub_trace_id', traceId)
    scope.setLevel('error' as Sentry.SeverityLevel)

    captureReportedError(error)
  })
}

export const handleRequestError = (_, eventType, error): EffectMap => {
  console.error(`Event of type ${eventType} failed.`)

  // relaunch event to prevent IOS connection bug
  if (error?.toString() === IOS_CONNECTION_ERROR) {
    return {
      dispatchN: [[COMPLETE_REQUEST, eventType], [eventType]],
    }
  }

  return {
    dispatchN: [
      [COMPLETE_REQUEST, eventType],
      [REPORT_ERROR, eventType, error],
    ],
  }
}

export const handleGetDeliveryTicket: EventHandler = (
  state: InitialState,
  params: IDeliveryTicketParams,
) => {
  if (!params.deliveryId) {
    return
  }

  state.loading.set(GET_DELIVERY_TICKET, true)

  return {
    graphQuery: {
      query: GetDeliveryTicketDocument,
      variables: {
        deliveryId: params.deliveryId,
      },
      onSuccess: [GET_DELIVERY_TICKET_RESULT, params.postActionSuccess],
      onError: [GET_DELIVERY_TICKET_RESULT, params.postActionError],
    },
  }
}

export const handleGetDeliveryTicketResult: EventHandler = (
  state,
  postAction: (ticket?: IDeliveryTicketResponse) => void,
  { getDeliveryTicket: deliveryTicket }: IQuery,
) => {
  state.loading.set(GET_DELIVERY_TICKET, false)

  if (postAction) {
    postAction(deliveryTicket)
  }
}

export function shouldPromptForPassword(rootStore: RootStore): boolean {
  const { auth, state } = rootStore

  // STX1-419: only force /save-password when password is the user's ONLY
  // available credential. Hybrid (SSO + password) and SSO-only tenants leave
  // password optional — the user can use SSO instead and set a password
  // voluntarily from profile.

  // STX1-1057: null is not "no password needed" — /auth/me never resolved
  // (in flight, or failed: loadAuthMe nulls every flag in its catch). Do not
  // prompt on absent data, and say so rather than sharing a silent branch
  // with a real "not required".
  if (auth.isPasswordRequired === null || auth.hasPassword === null) {
    console.warn(
      'Set-password prompt skipped: /auth/me has not resolved ' +
        `(isPasswordRequired=${auth.isPasswordRequired}, ` +
        `hasPassword=${auth.hasPassword}). A user whose only way in is a ` +
        'password will not be prompted until it does.',
    )
    return false
  }

  if (!auth.isPasswordRequired || auth.hasPassword) {
    return false
  }

  // Dismissal lasts for this session only, so a user who is not ready now is
  // asked again next sign-in rather than never again (STX1-1057).
  return !state.isSavePasswordPromptDismissed
}

export function emptyHandler() {
  /* empty handler */
}
